import { Button, Card, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, Globe, NotebookPen, RefreshCw, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DataCollectionResponse } from "@/api";
import {
  deleteFileMutation,
  errorMessage,
  getDataSourceQueryKey,
  listDataSourcesQueryKey,
  syncDataSourceMutation,
  updateDataSourceMutation,
  uploadFilesMutation,
} from "@/api";
import { Text } from "@/components/Text";
import { AutomatedSyncCard } from "./AutomatedSyncCard";
import { AddNoteModal, AddUrlModal, EditSelectionModal, FileRow } from "./ContentModals";
import { sharePointConfig } from "./NewDataSourcePage";
import { SyncScheduleCard } from "./SyncScheduleCard";

/**
 * Content tab: the collection's files/notes with type-appropriate add
 * actions (upload / add URL / add note) and a manual Sync for connected
 * sources. Files are a simple row list for now — the design-system Table
 * (needed for sortable columns) is tracked as a gap.
 */
export function DataSourceContentTab({
  source,
  previewUuid,
  onPreview,
}: Readonly<{
  source: DataCollectionResponse;
  previewUuid: string | null;
  onPreview: (fileUuid: string) => void;
}>) {
  const { t } = useTranslation("dataSources");
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [editSelectionOpen, setEditSelectionOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getDataSourceQueryKey({ path: { collection_uuid: source.uuid } }) });

  const upload = useMutation({
    ...uploadFilesMutation(),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("content.fileUploaded"));
    },
    onError: (e) => toast.error(errorMessage(e, t("content.uploadFailed"))),
  });
  const sync = useMutation({
    ...syncDataSourceMutation(),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("content.syncStarted"));
    },
    onError: (e) => toast.error(errorMessage(e, t("content.syncFailed"))),
  });
  const deleteFile = useMutation({
    ...deleteFileMutation(),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("content.fileDeleted"));
    },
    onError: (e) => toast.error(errorMessage(e, t("content.deleteFileFailed"))),
  });
  const updateSelection = useMutation({
    ...updateDataSourceMutation(),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: listDataSourcesQueryKey() });
      toast.success(t("content.selectionUpdated"));
      setEditSelectionOpen(false);
    },
    onError: (e) => toast.error(errorMessage(e, t("content.selectionFailed"))),
  });

  const files = source.files ?? [];
  const canSync = source.sourceType === "website" || source.sourceType === "sharepoint";

  return (
    <div className="flex flex-col gap-4">
      <AutomatedSyncCard source={source} />
      <SyncScheduleCard source={source} />
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Text variant="body">
              {t("content.files")} · {t("content.fileCount", { count: source.fileCount })}
            </Text>
            <div className="flex items-center gap-2">
              {canSync && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<RefreshCw size={14} />}
                  loading={sync.isPending}
                  onClick={() => sync.mutate({ path: { collection_uuid: source.uuid } })}
                >
                  {t("content.syncNow")}
                </Button>
              )}
              {source.sourceType === "upload" && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Upload size={14} />}
                  loading={upload.isPending}
                  onClick={() => fileInput.current?.click()}
                >
                  {t("content.addFiles")}
                </Button>
              )}
              {source.sourceType === "website" && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Globe size={14} />}
                  onClick={() => setUrlOpen(true)}
                >
                  {t("content.addUrl")}
                </Button>
              )}
              {source.sourceType === "sharepoint" && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Database size={14} />}
                  onClick={() => setEditSelectionOpen(true)}
                >
                  {t("content.editSelection")}
                </Button>
              )}
              {source.sourceType === "notes" && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<NotebookPen size={14} />}
                  onClick={() => setNoteOpen(true)}
                >
                  {t("content.addNote")}
                </Button>
              )}
            </div>
          </div>

          {files.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border-subtle">
              {files.map((file) => (
                <FileRow
                  key={file.uuid}
                  file={file}
                  onDelete={() => deleteFile.mutate({ path: { collection_uuid: source.uuid, file_uuid: file.uuid } })}
                  deleteLabel={t("content.deleteFile", { name: file.filename })}
                  onPreview={() => onPreview(file.uuid)}
                  selected={file.uuid === previewUuid}
                />
              ))}
            </ul>
          ) : (
            <Text variant="body-sm" tone="tertiary">
              {t("content.empty")}
            </Text>
          )}
        </div>

        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            const chosen = Array.from(e.target.files ?? []);
            if (chosen.length > 0) upload.mutate({ path: { collection_uuid: source.uuid }, body: { files: chosen } });
            e.target.value = "";
          }}
        />
        <AddUrlModal source={source} open={urlOpen} onClose={() => setUrlOpen(false)} onDone={invalidate} />
        <AddNoteModal source={source} open={noteOpen} onClose={() => setNoteOpen(false)} onDone={invalidate} />
        {source.sourceType === "sharepoint" && (
          <EditSelectionModal
            open={editSelectionOpen}
            onClose={() => setEditSelectionOpen(false)}
            saving={updateSelection.isPending}
            onSave={(sel) =>
              updateSelection.mutate({
                path: { collection_uuid: source.uuid },
                body: { config: sharePointConfig(sel) },
              })
            }
          />
        )}
      </Card>
    </div>
  );
}
